// frontend/src/pages/WalletSearch.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMagnifyingGlass, FaGlobe, FaArrowLeft } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import axios from '../utils/axiosConfig';
import API_URL from '../config';

function WalletSearch({ darkMode }) {
  const { t } = useTranslation();
  const [walletAddress, setWalletAddress] = useState('');
  const [chainId, setChainId] = useState(1); // 預設為 ETH (chainId: 1)
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [currentTokenPage, setCurrentTokenPage] = useState(1);
  const [currentTxPage, setCurrentTxPage] = useState(1);
  const [blacklistData, setBlacklistData] = useState([]);
  const [showValuableTokens, setShowValuableTokens] = useState(false);
  const [showFullLabelContent, setShowFullLabelContent] = useState(false);
  const [label, setLabel] = useState('scam'); // 新增 label state，預設值為 'scam'
  const ITEMS_PER_PAGE = 10;
  const [reporterCount, setReporterCount] = useState(0);

  // 鏈 ID 對應表
  const chainMap = {
    // '-3': 'Solana',
    195: 'TRON',
    0: 'Bitcoin',
    1: 'Ethereum',
    // 10: 'Optimism',
    // 25: 'Cronos',
    56: 'BSC',
    // 100: 'Gnosis',
    // 137: 'Polygon',
    // 169: 'Manta Pacific',
    // 199: 'Bittorrent',
    // 250: 'Fantom',
    // 288: 'Boba',
    // 324: 'zkSync Era',
    // 1024: 'CLV',
    // 1101: 'Polygon zkEVM',
    // 1111: 'Wemix',
    // 1284: 'Moonbeam',
    // 1285: 'Moonriver',
    // 5000: 'Mantle',
    // 8453: 'Base',
    // 42161: 'Arbitrum',
    // 42220: 'Celo',
    // 43114: 'Avalanche',
    // 59144: 'Linea',
    // 81457: 'Blast',
    // 1313161554: 'Aurora',
    784: 'Sui'
  };

  // 定義各鏈的地址規則，用於自動判斷
  const chainAddressRules = {
    // 非 EVM 鏈（獨特格式）
    195: {
      regex: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
      name: 'TRON',
      priority: 1 // 最高優先級，因為格式獨特
    },
    0: {
      regex: /^(1[1-9A-HJ-NP-Za-km-z]{25,34}|3[1-9A-HJ-NP-Za-km-z]{25,34}|bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39})$/,
      name: 'Bitcoin',
      priority: 2,
      exclude: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ // 排除 TRON 地址
    },
    // '-3': {
    //   regex: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    //   name: 'Solana',
    //   priority: 3,
    //   exclude: /^(T[1-9A-HJ-NP-Za-km-z]{33}|bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39})$/ // 排除 TRON 和 Bitcoin Bech32
    // },
    '784': {
      regex: /^0x[a-fA-F0-9]{64}$/,
      name: 'Sui',
      priority: 2, // 高優先級，因為格式獨特
    },
    // EVM 兼容鏈（統一格式）
    'evm': {
      regex: /^0x[a-fA-F0-9]{40}$/,
      name: 'EVM Compatible',
      priority: 4,
      chains: [
        { chainId: '1', name: 'Ethereum' },
        { chainId: '56', name: 'BSC' }
        // 其他 EVM 鏈暫時註解掉
        // { chainId: '10', name: 'Optimism' },
        // { chainId: '25', name: 'Cronos' },
        // { chainId: '100', name: 'Gnosis' },
        // { chainId: '137', name: 'Polygon' },
        // { chainId: '169', name: 'Manta Pacific' },
        // { chainId: '199', name: 'Bittorrent' },
        // { chainId: '250', name: 'Fantom' },
        // { chainId: '288', name: 'Boba' },
        // { chainId: '324', name: 'zkSync Era' },
        // { chainId: '1024', name: 'CLV' },
        // { chainId: '1101', name: 'Polygon zkEVM' },
        // { chainId: '1111', name: 'Wemix' },
        // { chainId: '1284', name: 'Moonbeam' },
        // { chainId: '1285', name: 'Moonriver' },
        // { chainId: '5000', name: 'Mantle' },
        // { chainId: '8453', name: 'Base' },
        // { chainId: '42161', name: 'Arbitrum' },
        // { chainId: '42220', name: 'Celo' },
        // { chainId: '43114', name: 'Avalanche' },
        // { chainId: '59144', name: 'Linea' },
        // { chainId: '81457', name: 'Blast' },
        // { chainId: '1313161554', name: 'Aurora' }
      ]
    }
  };

  // 自動判斷鏈的函數
  const detectChain = (address) => {
    if (!address) return 1; // 如果地址為空，預設為 Ethereum
    for (const [id, rule] of Object.entries(chainAddressRules)) {
      const matches = rule.regex.test(address);
      const excluded = rule.exclude ? rule.exclude.test(address) : false;
      if (matches && !excluded) {
        if (id === 'evm') {
          // 如果是 EVM 地址，返回第一個鏈（Ethereum）作為預設
          return parseInt(rule.chains[0].chainId); // 預設返回 Ethereum (1)
        }
        return parseInt(id);
      }
    }
    return 1; // 如果無法匹配，預設為 Ethereum
  };

  // 當 walletAddress 變化時，自動判斷鏈
  useEffect(() => {
    if (walletAddress) {
      const detectedChainId = detectChain(walletAddress);
      setChainId(detectedChainId);
    }
  }, [walletAddress]);

  // 檢查使用者認證狀態
  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await axios.get(`${API_URL}/api/current_user`, { withCredentials: true });
        if (response.data) {
          setIsAuthenticated(true);
          setUserId(response.data._id);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    }
    checkAuth();
  }, []);

  // 更新地址驗證函數以使用新的鏈檢測規則
  const validateAddress = (input) => {
    const isURL = input.includes('http') || input.includes('.');
    if (isURL) {
      setError(t('queryFailed'));
      return false;
    }

    // 使用我們的鏈檢測規則來驗證地址
    for (const [id, rule] of Object.entries(chainAddressRules)) {
      const matches = rule.regex.test(input);
      const excluded = rule.exclude ? rule.exclude.test(input) : false;
      if (matches && !excluded) {
        return true;
      }
    }
    
    setError(t('enterValidWalletAddress'));
    return false;
  };

  // 查詢錢包風險
  const handleSearch = async () => {
    setError('');
    setResult(null);
    if (!validateAddress(walletAddress)) return;

    setLoading(true);
    try {
      const lookupResponse = await axios.get(`${API_URL}/api/wallet/lookup`, {
        params: { chainId, walletAddress }
      });

      if (!lookupResponse.data.success) {
        throw new Error(lookupResponse.data.error || '查詢失敗');
      }

      const responseData = lookupResponse.data;
      console.log('Received lookup response:', responseData);

      // 處理風險標籤 - 提取標籤名稱和實體
      const riskLabels = [];
      let mainEntity = '';
      let nameTag = '';
      
      if (responseData.riskLabels && responseData.riskLabels.data) {
        const labelsData = responseData.riskLabels.data;
        
        // 提取主要實體和標籤
        mainEntity = labelsData.main_entity || '';
        // 提取名稱標籤
        nameTag = labelsData.name_tag || '';
        
        console.log('Risk labels data:', labelsData);
        
        // 提取標籤
        if (labelsData.labels && Array.isArray(labelsData.labels)) {
          labelsData.labels.forEach(label => {
            if (label.name) {
              // 美化標籤顯示
              let displayName = label.name;
              
              // 自定義標籤顯示邏輯
              if (displayName === "SANCTIONED_ENTITY") {
                displayName = "制裁實體";
              } else {
                // 一般處理：將下劃線改為空格，每個單詞首字母大寫
                displayName = displayName.replace(/_/g, ' ').toLowerCase()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              }
              
              // 避免重複添加標籤
              if (!riskLabels.includes(displayName)) {
                riskLabels.push(displayName);
              }
            }
          });
        }
        
        // 如果沒有標籤但有屬性，提取屬性
        if (riskLabels.length === 0 && labelsData.attributes && Array.isArray(labelsData.attributes) && labelsData.attributes.length > 0) {
          labelsData.attributes.forEach(attr => {
            if (attr.key) {
              const displayName = attr.key.replace(/_/g, ' ');
              if (!riskLabels.includes(displayName)) {
                riskLabels.push(displayName);
              }
            }
          });
        }
      }
      
      // 處理制裁狀態 - 提取制裁信息並添加到標籤中
      const isSanctioned = responseData.sanctionStatus?.is_sanctioned || false;
      const sanctionSources = [];
      
      if (isSanctioned) {
        // 如果是制裁地址但尚未添加制裁標籤，添加一個
        if (!riskLabels.includes("制裁實體")) {
          riskLabels.push("制裁實體");
        }
        
        // 如果主實體為空但有制裁信息，設置主實體
        if (!mainEntity && responseData.sanctionStatus?.identifications && responseData.sanctionStatus.identifications.length > 0) {
          mainEntity = responseData.sanctionStatus.identifications[0].name || "SANCTIONED ENTITY";
        }
        
        // 提取制裁來源
        if (responseData.sanctionStatus?.identifications && responseData.sanctionStatus.identifications.length > 0) {
          responseData.sanctionStatus.identifications.forEach(item => {
            if (item.name) {
              sanctionSources.push(item.name);
              
              // 檢查是否包含OKO Design Bureau
              if (item.name.includes('OKO Design Bureau') && !riskLabels.includes("Oko Design Bureau")) {
                riskLabels.push("Oko Design Bureau");
              }
            } else if (item.category) {
              sanctionSources.push(item.category);
            }
          });
        }
      }

      // 處理合規分數
      const complianceScore = responseData.complianceScore?.risk_score || 
                             (responseData.riskAssessment?.complianceScore || 0);

      // 創建風險評估對象
      const riskAssessment = {
        riskScore: responseData.riskAssessment?.riskScore || 0,
        riskLevel: responseData.riskAssessment?.riskLevel || 'low',
        riskFactors: responseData.riskAssessment?.riskFactors || [],
        action: responseData.riskAssessment?.action || 'Normal Monitoring',
        complianceScore: complianceScore,
        isSanctioned: isSanctioned,
        sanctionSources: sanctionSources,
        transactionStats: responseData.riskAssessment?.transactionStats || {
          totalTransactions: responseData.transactions.length || 0,
          recentTransactions: 0,
          largeTransactions: 0,
          stablecoinRatio: 0
        }
      };
      
      const reporterCount = responseData.reporterCount || 0;
      setReporterCount(reporterCount);

      setResult({
        chainId: responseData.chainId,
        walletAddress: responseData.walletAddress,
        transactions: responseData.transactions.map(tx => ({
          txHash: tx.txHash || tx.hash || tx.txId || '',
          from: tx.from || '',
          to: tx.to || '',
          value: parseFloat(tx.value || tx.amount || '0').toString(),
          tokenSymbol: tx.tokenSymbol || 'ETH',
          timestamp: typeof tx.timestamp === 'number' ? tx.timestamp : new Date(tx.timestamp).getTime(),
          state: tx.state || tx.status || 'success',
          txFee: parseFloat(tx.txFee || tx.fee || '0'),
          blockNumber: tx.blockNumber || 0,
          gasPrice: tx.gasPrice || '0',
          gasUsed: tx.gasUsed || '0',
          status: tx.status || 'success',
          methodId: tx.methodId || '',
          blockHash: tx.blockHash || '',
          height: tx.height || '',
          isContract: tx.isContract || false,
          isStableCoin: tx.isStableCoin || false,
          riskScore: tx.riskScore || 0,
          riskFactors: tx.riskFactors || [],
          riskLabels: tx.riskLabels || []
        })),
        tokenBalances: responseData.tokenBalances || [],
        riskScore: responseData.riskAssessment?.riskScore || 0,
        riskLevel: responseData.riskAssessment?.riskLevel || 'low',
        riskFactors: responseData.riskAssessment?.riskFactors || [],
        riskLabels,
        mainEntity,
        nameTag,
        sanctionStatus: responseData.sanctionStatus,
        sanctionSources,
        complianceScore: complianceScore,
        riskAssessment: {
          riskScore: responseData.riskAssessment?.riskScore || 0,
          riskLevel: responseData.riskAssessment?.riskLevel || 'low',
          riskFactors: responseData.riskAssessment?.riskFactors || [],
          riskLabels,
          mainEntity,
          nameTag,
          action: responseData.riskAssessment?.action || 'Normal Monitoring',
          complianceScore: complianceScore,
          isSanctioned: isSanctioned,
          sanctionSources: sanctionSources,
          transactionStats: {
            totalTransactions: responseData.riskAssessment?.transactionStats?.totalTransactions || 0,
            recentTransactions: responseData.riskAssessment?.transactionStats?.recentTransactions || 0,
            largeTransactions: responseData.riskAssessment?.transactionStats?.largeTransactions || 0,
            stablecoinRatio: (responseData.riskAssessment?.transactionStats?.stablecoinRatio || 0) * 100,
            activeTimezones: responseData.riskAssessment?.transactionStats?.activeTimezones || 0
          }
        },
        reporterCount: reporterCount
      });
    } catch (err) {
      console.error('Error in handleSearch:', err);
      // 處理速率限制錯誤（429）
      if (err.response?.status === 429) {
        const errorMessage = err.response?.data?.message || '您今天的查詢次數已達上限，請稍後再試。';
        setError(errorMessage);
        toast.error(errorMessage, { duration: 5000 });
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || t('queryFailed'));
        toast.error(err.response?.data?.message || err.response?.data?.error || t('queryFailed'));
      }
    } finally {
      setLoading(false);
    }
  };
 // 新增至白名單功能
  const handleAddToWhitelist = async () => {
    if (!walletAddress) {
      toast.error(t('enterValidWalletAddress'));
      return;
    }
    if (!isAuthenticated) {
      window.location.href = `${API_URL}/api/auth/google`;
      return;
    }
    try {
      await axios.post(`${API_URL}/api/whitelist`, { walletAddress, userId, chainId: chainId.toString(), comment });
      toast.success(t('walletAddedToWhitelist'));
    } catch (error) {
      toast.error(t('errorAddingWalletToWhitelist'));
    }
  };
  // 新增至黑名單功能
  const handleAddToBlacklist = async () => {
    if (!walletAddress) {
      toast.error(t('enterValidWalletAddress'));
      return;
    }
    if (!isAuthenticated) {
      window.location.href = `${API_URL}/api/auth/google`;
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/blacklist`, {
        walletAddress,
        riskLevel: 'low',// 預設風險等級
        blackScore: 1,    // 初始分數
        labels: [label],  // 選定標籤
        comment,          // 備註
        userId,
        chainId,
        riskFactors: Array.isArray(result.riskFactors) ? result.riskFactors : [],
        riskLevel: result.riskLevel || 'low',
        riskScore: typeof result.riskScore === 'number' ? result.riskScore : 0
      }, { withCredentials: true });

      // 更新用戶資訊中的「舉報的可疑錢包」
      const userResponse = await axios.post(`${API_URL}/api/add_suspicious_wallet_to_user`, {
        userId,
        walletAddress,
        label,
        comment
      });

      if (response.data.success) {
        if (userResponse.data.alreadyReported) {
          toast.info(userResponse.data.message);
        } else {
          toast.success(t('walletAddedToBlacklist'));
          // 更新本地狀態中的報告人數
          setResult(prev => ({
            ...prev,
            reporterCount: response.data.reporterCount || (prev.reporterCount || 0) + 1
          }));
        }
        setBlacklistData(prev => [...prev, {
          walletAddress,
          userId,
          riskFactors: Array.isArray(result.riskFactors) ? result.riskFactors : [],
          riskLevel: result.riskLevel || 'low',
          riskScore: typeof result.riskScore === 'number' ? result.riskScore : 0,
          comment,
          chainId: chainId.toString(),
          createdAt: new Date()
        }]);
      }
    } catch (error) {
      if (error.response && error.response.status === 400 && error.response.data.error === "已經回報過該地址") {
        toast.error(error.response.data.error);
      } else if (error.response && error.response.data && error.response.data.details) {
        toast.error(JSON.stringify(error.response.data.details));
      } else {
        toast.error(t('errorAddingWalletToBlacklist'));
      }
    }
  };

  // 過濾有價值的代幣（priceUsd > 0）
  const filteredTokenBalances = showValuableTokens
    ? (result?.tokenBalances || []).filter(token => {
        const price = parseFloat(token.priceUsd);
        return !isNaN(price) && price > 0;
      })
    : (result?.tokenBalances || []);

  // 计算分页数据
  const getPaginatedData = (data, page, itemsPerPage) => {
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return data.slice(start, end);
  };

  // 计算总页数
  const getTotalPages = (totalItems) => {
    return Math.ceil(totalItems / ITEMS_PER_PAGE);
  };

  // 分页组件
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    // 只显示前2、后2、当前页前后各2页
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 2 && i <= currentPage + 2)
      ) {
        pageNumbers.push(i);
      } else if (
        (i === 2 && currentPage > 4) ||
        (i === totalPages - 1 && currentPage < totalPages - 3)
      ) {
        pageNumbers.push('...');
      }
    }

    let lastPage = 0;
    return (
      <div className="flex justify-center items-center space-x-1 mt-4">
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded-full ${currentPage === 1 ? 'bg-gray-200 text-gray-400' : 'bg-white text-blue-600 hover:bg-blue-100'} border`}
        >
          &lt;
        </button>
        {pageNumbers.map((num, idx) => {
          if (num === '...') {
            return (
              <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
            );
          }
          lastPage = num;
          return (
            <button
              key={`page-${num}`}
              onClick={() => onPageChange(num)}
              className={`px-3 py-1 rounded-full border transition ${
                currentPage === num
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-blue-600 hover:bg-blue-100 border-gray-300'
              }`}
            >
              {num}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded-full ${currentPage === totalPages ? 'bg-gray-200 text-gray-400' : 'bg-white text-blue-600 hover:bg-blue-100'} border`}
        >
          &gt;
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* 頂部藍色漸層背景與標題 */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-center py-6">
        <h1 className="text-3xl font-bold">{t('walletRiskQuery')}</h1>
      </div>

      {/* 搜尋框區域 */}
      <div className="flex flex-col items-center py-6 bg-gray-100 dark:bg-gray-900">
        <div className="w-full max-w-3xl px-4 flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="w-full md:flex-1 relative">
            <input
              type="text"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder={t('inputWalletAddress')}
              className="w-full p-3 pl-10 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
            />
            <FaMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          </div>
          <select
            value={chainId}
            onChange={(e) => setChainId(parseInt(e.target.value))}
            className="w-full md:w-auto p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          >
            {/* <option value="-3">Solana</option> */}
            <option value="195">TRON</option>
            <option value="0">Bitcoin</option>
            <option value="1">Ethereum</option>
            {/* <option value="10">Optimism</option> */}
            {/* <option value="25">Cronos</option> */}
            <option value="56">BSC</option>
            {/* <option value="100">Gnosis</option> */}
            {/* <option value="137">Polygon</option> */}
            {/* <option value="169">Manta Pacific</option> */}
            {/* <option value="199">Bittorrent</option> */}
            {/* <option value="250">Fantom</option> */}
            {/* <option value="288">Boba</option> */}
            {/* <option value="324">zkSync Era</option> */}
            {/* <option value="1024">CLV</option> */}
            {/* <option value="1101">Polygon zkEVM</option> */}
            {/* <option value="1111">Wemix</option> */}
            {/* <option value="1284">Moonbeam</option> */}
            {/* <option value="1285">Moonriver</option> */}
            {/* <option value="5000">Mantle</option> */}
            {/* <option value="8453">Base</option> */}
            {/* <option value="42161">Arbitrum</option> */}
            {/* <option value="42220">Celo</option> */}
            {/* <option value="43114">Avalanche</option> */}
            {/* <option value="59144">Linea</option> */}
            {/* <option value="81457">Blast</option> */}
            {/* <option value="1313161554">Aurora</option> */}
            <option value="784">Sui</option>
          </select>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full md:w-auto px-6 py-3 bg-blue-500 text-white rounded-lg shadow-sm hover:bg-blue-600 disabled:bg-gray-400 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            {loading ? '查詢中...' : t('search')}
          </button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg w-full max-w-3xl">
            {error}
          </div>
        )}
      </div>

      {/* 結果卡片區域 */}
      {result && (
        <div className="flex-1 flex justify-center p-6 bg-gray-100 dark:bg-gray-900">
          <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            {/* 標題與標籤 */}
            <div className="flex flex-col md:flex-row items-center justify-between mb-4">
              <div className="flex items-center space-x-2 mb-4 md:mb-0">
                <FaGlobe className="text-gray-600 dark:text-gray-400" />
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                  {t('walletRiskDetails')}
                </h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm ${
                result.riskAssessment.isSanctioned ? 
                'bg-red-100 text-red-800' : 
                'bg-green-100 text-green-800'
              }`}>
                {result.riskAssessment.isSanctioned ? t('sanctioned') : t('confirmed')}
              </span>
            </div>

            {/* 風險評分與等級 */}
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-400">
                {chainMap[chainId]} Transaction • {new Date().toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}
              </p>
              <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4">
                <p className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
                  {t('riskScore')}: {result.riskScore}（{t('higherScoreHigherRisk')}, 0-100）
                </p>
                <span className={`text-lg font-normal ${
                  result.riskLevel === 'critical' ? 'text-red-800' :
                  result.riskLevel === 'high' ? 'text-red-600' :
                  result.riskLevel === 'medium' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  ({result.riskLevel === 'critical' ? t('criticalRisk') :
                    result.riskLevel === 'high' ? t('highRisk') :
                    result.riskLevel === 'medium' ? t('mediumRisk') :
                    t('lowRisk')})
                </span>
              </div>
              {result.complianceScore && typeof result.complianceScore.risk_score === 'number' && result.complianceScore.risk_score > 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
                  合規風險分數（BlockSec API）：{result.complianceScore.risk_score}（分數越高風險越大，範圍 1-5）
                  {result.complianceScore.risk_score === 5 && result.riskAssessment.isSanctioned && (
                    <span className="text-red-600 ml-2">（制裁地址）</span>
                  )}
                </p>
              )}
              {result.riskAssessment.isSanctioned && (
                <p className="text-red-600 dark:text-red-400 mt-2">
                  {t('sanctioned')}
                </p>
              )}
            </div>

            {/* 地址資訊 */}
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-400 font-medium">{t('walletAddress')}</p>
              <p className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-800 dark:text-gray-200 break-all">
                {result.walletAddress}
              </p>
            </div>

            {/* 風險標籤 */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-gray-600 dark:text-gray-400 font-medium">{t('walletLabels') || '錢包標籤查詢結果'}</p>
                <label className="flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showFullLabelContent}
                    onChange={() => {
                      setShowFullLabelContent(!showFullLabelContent);
                      if (!showFullLabelContent) {
                        setCurrentTokenPage(1);
                      }
                    }}
                    className="form-checkbox h-5 w-5 text-blue-600 transition duration-150 ease-in-out rounded focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-gray-800 dark:border-gray-600"
                  />
                  <span className="ml-2 text-gray-700 dark:text-gray-300">
                    {showFullLabelContent ? t('hideDetailedContent') || '隱藏詳細內容' : t('showDetailedContent') || '顯示詳細內容'}
                  </span>
                </label>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {result.mainEntity && (
                  <div className="mb-2">
                    <span className="font-medium text-gray-800 dark:text-gray-200">{t('mainEntity')}: </span>
                    <span className="text-gray-700 dark:text-gray-300">{result.mainEntity}</span>
                  </div>
                )}
                
                {result.nameTag && (
                  <div className="mb-2">
                    <span className="font-medium text-gray-800 dark:text-gray-200">標籤: </span>
                    {showFullLabelContent ? (
                      <span className="text-gray-700 dark:text-gray-300">{result.nameTag}</span>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">
                        {result.nameTag.split('\n\n')[0]}
                      </span>
                    )}
                  </div>
                )}
                
                {result.riskLabels && result.riskLabels.length > 0 && (
                  <div className="mt-2">
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {result.sanctionStatus && result.sanctionStatus.is_sanctioned ? t('sanctionLabels') : t('riskLabels')}: 
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {result.riskLabels.map((label, index) => (
                        <span key={index} className={`px-3 py-1 ${
                          label === "制裁實體" || label === "Oko Design Bureau" 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        } rounded-full text-sm`}>
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {!result.mainEntity && !result.nameTag && (!result.riskLabels || result.riskLabels.length === 0) && (
                  <div className="text-gray-500 dark:text-gray-400">未找到相關標籤</div>
                )}
              </div>
            </div>

            {/* 制裁檢查結果 */}
            {result.sanctionStatus && result.sanctionStatus.is_sanctioned && (
              <div className="mb-4">
                <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">{t('sanctionCheck') || '制裁檢查結果'}</p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 font-medium mb-2">該錢包地址已受到制裁。</p>
                  
                  {result.mainEntity && result.mainEntity.includes('OKO Design Bureau') && (
                    <div className="mb-2">
                      <p className="text-red-600 font-medium">制裁實體: Oko Design Bureau</p>
                    </div>
                  )}
                  
                  {result.nameTag && (
                    <div className="mb-3">
                      <p className="text-red-600 text-sm">{result.nameTag.split('\n\n')[0]}</p>
                    </div>
                  )}
                  
                  {result.sanctionSources && result.sanctionSources.length > 0 && (
                    <div>
                      <p className="text-red-600 font-medium mb-1">制裁來源:</p>
                      <ul className="list-disc pl-5">
                        {result.sanctionSources.map((source, index) => (
                          <li key={index} className="text-red-600">{source}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 報告人數統計 */}
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">{t('reportStatistics')}</p>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300">
                  {result.reporterCount > 0 ? (
                    <>
                      <span className="font-medium">{result.reporterCount}</span> {result.reporterCount === 1 ? t('reportedAsSuspiciousSingle') : t('reportedAsSuspicious')}
                    </>
                  ) : (
                    t('noReports')
                  )}
                </p>
              </div>
            </div>

            {/* 顯示代幣餘額 */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-gray-600 dark:text-gray-400 font-medium">{t('tokenBalances')}</h4>
                <label className="flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showValuableTokens}
                    onChange={() => {
                      setShowValuableTokens(!showValuableTokens);
                      if (!showValuableTokens) {
                        setCurrentTokenPage(1);
                      }
                    }}
                    className="form-checkbox h-5 w-5 text-blue-600 transition duration-150 ease-in-out rounded focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-gray-800 dark:border-gray-600"
                  />
                  <span className="ml-2 text-gray-700 dark:text-gray-300">
                    {t('showValuableTokens')}
                  </span>
                </label>
              </div>
              {/* 代幣餘額表格或無代幣訊息 */}
              {filteredTokenBalances.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('symbol')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('tokenamount')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('price')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('value')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {getPaginatedData(filteredTokenBalances, currentTokenPage, ITEMS_PER_PAGE).map((token, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{token.symbol}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{token.holdingAmount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                            {token.priceUsd !== undefined && token.priceUsd !== null && !isNaN(parseFloat(token.priceUsd))
                              ? `$${parseFloat(token.priceUsd).toFixed(2)}`
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                            {token.valueUsd !== undefined && token.valueUsd !== null && !isNaN(parseFloat(token.valueUsd))
                              ? `$${parseFloat(token.valueUsd).toFixed(2)}`
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {getTotalPages(filteredTokenBalances.length) > 1 && (
                    <Pagination
                      currentPage={currentTokenPage}
                      totalPages={getTotalPages(filteredTokenBalances.length)}
                      onPageChange={setCurrentTokenPage}
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 12h4m0 0h4m-4 0v4m0-4V8" />
                  </svg>
                  <span className="text-gray-500 dark:text-gray-300">{t('noTokensFound')}</span>
                </div>
              )}
            </div>

            {/* 顯示交易列表 */}
            <div className="mb-4">
              <h4 className="text-gray-600 dark:text-gray-400 font-medium mb-2">{t('transactions')}</h4>
              {result.transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('txHash')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('from')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('to')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('tokenamount')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('tokenSymbol')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('timestamp')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('state')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {getPaginatedData(result.transactions, currentTxPage, ITEMS_PER_PAGE).map((tx, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                            <a
                              href={`https://etherscan.io/tx/${tx.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              {tx.txHash.slice(0, 10)}...
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{tx.from.slice(0, 10)}...</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{tx.to.slice(0, 10)}...</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{tx.value}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{tx.tokenSymbol}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                            {new Date(tx.timestamp).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{tx.state}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {getTotalPages(result.transactions.length) > 1 && (
                    <Pagination
                      currentPage={currentTxPage}
                      totalPages={getTotalPages(result.transactions.length)}
                      onPageChange={setCurrentTxPage}
                    />
                  )}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">{t('noTransactionsFound')}</p>
              )}
            </div>

            {/* 備註與按鈕 */}
            <div className="mt-6 flex flex-col space-y-4">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('enterComment')}
                className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                <button
                  onClick={handleAddToWhitelist}
                  className="w-full md:w-auto px-4 py-2 bg-green-500 text-white rounded-lg shadow-md hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                >
                  {t('thisIsMyWallet')}
                </button>
                <button
                  onClick={handleAddToBlacklist}
                  className="w-full md:w-auto px-4 py-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700"
                >
                  {t('thisIsSuspiciousWallet')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 返回按鈕 */}
      {result && (
        <button
          onClick={() => setResult(null)}
          className="fixed bottom-6 right-6 p-4 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          <FaArrowLeft />
        </button>
      )}
    </div>
  );
}

export default WalletSearch;